<template>
    <div class="page-wrap">
        <div class="page-bar">
            <div>
                <div class="page-bar-sub">Settings</div>
                <div class="page-bar-main">Standard Users<info-tip text="Standard users are the log ins for the non-admin users of your Ironbook. They see published items for their locations and departments. When you add a standard user or give access to a registration, they're linked to the employee record with the same phone number, or a new employee record is created and opened so you can complete it. Links can be changed from the employee's page." /></div>
            </div>
            <!-- How admin users, standard users, employees and teams fit together (docs/permissions-guide.html, served from public/), in the PDF popup -->
            <div class="btn-light" @click="() => { pdfURL = '/ironbook-permissions-guide.pdf'; showPDF = true; }">Permissions Guide</div>
        </div>
        <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
            <div class="page-filters pf-bar">
                <div class="pf-search">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" /></svg>
                    <input type="text" v-model="searchQ" @input="search()" placeholder="Search" aria-label="Search" />
                </div>
                <select v-model="active">
                    <option :value="true">Active</option>
                    <option :value="false">Inactive</option>
                </select>
                <select v-if="$store.state.user.isAdmin" v-model="selectedLocation" @change="search()">
                    <option value="ALL">All Locations</option>
                    <option v-for="(loc, i) in $store.state.user.locations" :key="i" :value="loc">{{ loc }}</option>
                </select>
                <span class="pf-spacer"></span>
                <div class="btn" @click="printSignUpSheet()">Sign-up Sheet</div>
                <div class="btn" @click="showInviteFn()">Send Invitation Text</div>
                <div v-if="$store.state.user.isAdmin" @click="() => { userToEdit = null; showAddEditUser = true }" class="add-btn">Add</div>
            </div>
            <div v-if="$store.state.user.isAdmin && awaitingAccess.length" class="intake-wrap" style="max-width:800px">
                <div class="intake-head">Awaiting Access</div>
                <div v-for="user in awaitingAccess" :key="user.ID" class="intake-row">
                    <div class="av">{{ initials(user) }}</div>
                    <div class="intake-info">
                        <div class="intake-name">{{ user.FirstName }} {{ user.LastName }}</div>
                        <div class="intake-sub"><span v-if="user.Location">{{ user.Location }} · </span>{{ formatPhone(user.Phone) }}</div>
                    </div>
                    <div class="intake-act">
                        <div class="btn" @click="editUser(user)">Give access</div>
                        <div class="btn btn-light" @click="deleteUser(user)">Delete</div>
                    </div>
                </div>
            </div>
            <table class="full-table">
                <tbody>
                    <tr class="table-labels">
                        <td>Name</td>
                        <td>Phone</td>
                        <td>Location</td>
                        <td>Last Login</td>
                        <td># of Visits</td>
                        <td style="padding:0"></td>
                    </tr>
                    <tr style="padding:0;height:0"></tr>
                    <template v-for="(user, i) in users" :key="i">
                        <tr v-if="(active && !user.InActive) || (!active && user.InActive)">
                            <td>{{ user.FirstName }} {{ user.LastName }}<span v-if="!user.EmployeeID" class="no-emp-badge" title="Not linked to a Team Hub employee. Link them from the employee's page (Sign-in accounts).">No employee</span><div v-if="user.EmployeeName" class="user-emp-sub">Employee: {{ user.EmployeeName }}</div></td>
                            <td><a :href="'tel:'+user.Phone" target="_blank">{{ formatPhone(user.Phone) }}</a></td>
                            <td>{{ user.Location }}<span v-if="user.LocationsAll.length > 1" :title="user.LocationsAll.slice(1).join(', ')" style="cursor:help"> +{{ user.LocationsAll.length - 1 }}</span></td>
                            <td>{{ user.LastLogin }}</td>
                            <td>{{ user.NumLogins }}</td>
                            <td style="text-align:center">
                                <div class="row-menu">
                                    <button type="button" class="row-menu-btn" @click.stop="toggleRowMenu(user)">
                                        <svg-icon icon="dots" />
                                    </button>
                                    <div v-if="openMenuID == user.ID" class="row-menu-dropdown">
                                        <a @click.prevent="rowMenuAction(() => getSignOffs(user))">Sign Offs</a>
                                        <template v-if="$store.state.user.isAdmin">
                                            <a @click.prevent="rowMenuAction(() => editUser(user))">Edit</a>
                                            <a @click.prevent="rowMenuAction(() => signInAsUser(user))">Sign In As</a>
                                            <div class="row-menu-divider"></div>
                                            <a class="row-menu-delete" @click.prevent="rowMenuAction(() => deleteUser(user))">Delete</a>
                                        </template>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr v-if="user.showSignOffs">
                            <td colspan="42">
                                <span v-if="!user.signOffs.length">No Sign Offs</span>
                                <div style="max-height:200px;overflow:auto">
                                    <div v-for="(signOff, j) in user.signOffs" :key="'r'+j">
                                        {{ signOff.DateDisplay }} - <a :href="'/playbook/'+signOff.Slug" target="_blank">{{ signOff.NodeName }}</a> - {{ signOff.Signature }}
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>

        <div v-if="showInvite" class="error-msg-wrap">
            <div class="form-wrapper slide-up">
                <div class="form-close">
                    <button type="button" class="form-close-x" title="Close" @click="() => { showInvite = false }">&times;</button>
                </div>
                <h3>Phone</h3>
                <phone-input v-model="invitePhone" placeholder="Phone" />
                <h3>Message</h3>
                <textarea v-model="inviteMsg" placeholder="Message"></textarea>
                <input :class="disableButton ? 'disable-button' : ''" type="submit" value="Send" @click="sendInvite()" />
            </div>
        </div>

        <pdf-pop-up v-if="showPDF" @close="() => { showPDF = false }" :url="pdfURL" />
        <add-edit-public-user v-if="showAddEditUser" :user="userToEdit" @close="() => { showAddEditUser = false; getUsers() }" />
        <confirm-delete v-if="showConfirmDelete" @confirm="deleteUserConfirm()" @cancel="() => { showConfirmDelete = false; }" msg="Are you sure you would like to delete this user?"/>
    </div>
</template>

<script lang="ts">
import { defineComponent, registerRuntimeCompiler } from 'vue';
import axios from 'axios';
import PhoneInput from '@/components/PhoneInput.vue';
import { normalizePhone, PHONE_ERROR } from '@/phone';
import AddEditPublicUser from '@/components/AddEditPublicUser.vue';
import ConfirmDelete from '@/components/ConfirmDelete.vue';
import SvgIcon from '@/components/SvgIcon.vue';
import PDFPopUp from '@/components/PDFPopUp.vue';
import InfoTip from '@/components/InfoTip.vue';

export default defineComponent({
    name: 'PublicUsers',
    components: {
        PhoneInput,
        AddEditPublicUser, ConfirmDelete, SvgIcon,
        PdfPopUp: PDFPopUp, InfoTip
    },
    data() {
        return {
            allUsers: [] as any,
            users: [] as any,
            searchQ: '',
            selectedLocation: 'ALL',
            openMenuID: null as any,
            userToEdit: null,
            showAddEditUser: false,
            showConfirmDelete: false,
            phoneToDelete: null as any,
            active: true,
            showInvite: false,
            invitePhone: '',
            inviteMsg: '',
            disableButton: false,
            pdfURL: '',
            showPDF: false
        }
    },
    computed: {
        // Registered but never approved: inactive and has never logged in (honors the location filter)
        awaitingAccess(): any[] {
            return this.allUsers.filter((p: any) => {
                return p.InActive && !p.LastLogin
                    && (this.selectedLocation == 'ALL' || p.LocationsAll.includes(this.selectedLocation));
            });
        }
    },
    async created() {
        await this.getUsers();

        this.$store.state.routeLoaded = true;
    },
    methods: {
        async getUsers() {
            await axios.get(`${this.$store.state.apiUrl}/api/publicusers`)
                .then(resp => {
                    for(const user of resp.data) {
                        // primary Location first, then any AdditionalLocationsJSON
                        user.LocationsAll = [user.Location];
                        if(user.AdditionalLocationsJSON) {
                            try {
                                user.LocationsAll.push(...JSON.parse(user.AdditionalLocationsJSON));
                            } catch(e) {
                                // nothing
                            }
                        }
                        if(user.LastLogin) {
                            user.LastLogin = new Date(user.LastLogin).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
                        }
                    }
                    this.allUsers = resp.data;
                    this.users = resp.data;
                })
                .catch(e => {
                    console.log(e);
                })

            this.search();
        },
        search() {
            let usersTemp = this.allUsers;
            if(this.selectedLocation != 'ALL') {
                usersTemp = usersTemp.filter((p: any) => {
                    return p.LocationsAll.includes(this.selectedLocation);
                })
            }
            if(!this.searchQ || this.searchQ.length < 3) {
                this.users = usersTemp;
                return;
            }
            const searchQLower = this.searchQ.toLowerCase();
            this.users = usersTemp.filter((p: any) => {
                return p.FirstName.toLowerCase().includes(searchQLower)
                    || p.LastName.toLowerCase().includes(searchQLower)
                    || p.Phone.includes(searchQLower)
            })
        },
        toggleRowMenu(user: any) {
            this.openMenuID = this.openMenuID == user.ID ? null : user.ID;
        },
        closeRowMenu() {
            this.openMenuID = null;
        },
        rowMenuAction(action: any) {
            this.closeRowMenu();
            action();
        },
        handleOutsideRowMenuClick(e: MouseEvent) {
            if(this.openMenuID && !(e.target as HTMLElement).closest('.row-menu')) {
                this.openMenuID = null;
            }
        },
        initials(user: any): string {
            return ((user.FirstName || '').charAt(0) + (user.LastName || '').charAt(0)).toUpperCase();
        },
        editUser(user: any) {
            this.userToEdit = user;
            this.showAddEditUser = true;
        },
        deleteUser(user: any) {
            this.phoneToDelete = user.Phone;
            this.showConfirmDelete = true;
        },
        async deleteUserConfirm() {
            await axios.delete(`${this.$store.state.apiUrl}/api/publicusers/${encodeURIComponent(this.phoneToDelete)}`)
                .then(resp => {
                    this.getUsers();
                    this.showConfirmDelete = false;
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error')
                })
        },
        signInAsUser(user: any) {
            axios.get(`${this.$store.state.apiUrl}/api/signinasuser?phone=${encodeURIComponent(user.Phone)}`)
                .then(resp => {
                    window.location.href = '/';
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error')
                })
        },
        async getSignOffs(user: any) {
            if(user.showSignOffs) {
                user.showSignOffs = false;
            } else {
                axios.get(`${this.$store.state.apiUrl}/api/signoffs/foruser/${encodeURIComponent(user.Phone)}`)
                    .then(resp => {
                        for(const signOff of resp.data) {
                            signOff.DateDisplay = new Date(signOff.Date).toLocaleDateString();
                        }
                        user.signOffs = resp.data;
                        user.showSignOffs = true;
                    })
                    .catch(e => {
                        console.log(e);
                        alert('There was an error')
                    })
            }
        },
        showInviteFn() {
            this.inviteMsg = `You are invited to join the ${this.$store.state.user.companyName} Ironbook! Please register at the link below\n\n${this.$store.state.user.domain}`;
            this.invitePhone = '';
            this.showInvite = true;
        },
        async sendInvite() {
            if(!this.inviteMsg) {
                alert('Include a message to be sent');
                return;
            }
            if(!this.invitePhone) {
                alert('Include a phone number to be sent to');
                return;
            }
            const invitePhone = normalizePhone(this.invitePhone);
            if(!invitePhone) {
                alert(PHONE_ERROR);
                return;
            }
            this.invitePhone = invitePhone;
            this.disableButton = true;
            const data = {
                inviteMsg: this.inviteMsg,
                invitePhone: this.invitePhone
            }
            await axios.post(`${this.$store.state.apiUrl}/api/publicusers/sendinvite`, data)
                .then(resp => {
                    this.showInvite = false;
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error')
                })
            this.disableButton = false;
        },
        printSignUpSheet() {
            axios.get(`${this.$store.state.apiUrl}/api/publicusers/signupsheet`)
                .then(resp => {
                    this.pdfURL = `${this.$store.state.apiUrl}/temppdf/${resp.data.fileName}`;
                    this.showPDF = true;
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error')
                })
        }
    },
    mounted() {
        document.addEventListener('click', this.handleOutsideRowMenuClick);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleOutsideRowMenuClick);
    }
});
</script>
